import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import type { SubscriptionStatus } from '../types';

interface SubscriptionStatusBadgeProps {
  status: SubscriptionStatus;
  className?: string;
}

const statusConfig: Record<SubscriptionStatus, { dot: string; badge: string; key: string }> = {
  trialing: {
    dot: 'bg-blue-500',
    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    key: 'subscriptions.status.trialing',
  },
  active: {
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    key: 'subscriptions.status.active',
  },
  past_due: {
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    key: 'subscriptions.status.past_due',
  },
  cancelled: {
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    key: 'subscriptions.status.cancelled',
  },
  expired: {
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    key: 'subscriptions.status.expired',
  },
  suspended: {
    dot: 'bg-yellow-500',
    badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    key: 'subscriptions.status.suspended',
  },
};

export function SubscriptionStatusBadge({ status, className }: SubscriptionStatusBadgeProps) {
  const { t } = useTranslation();
  const config = statusConfig[status] ?? statusConfig.cancelled;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium',
        config.badge,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', config.dot)} aria-hidden="true" />
      {t(config.key)}
    </span>
  );
}
